/**
 * Order summary — cart items + totals breakdown
 * Used by desktop sidebar and mobile expandable panel.
 */

import { Separator, PriceLine } from '@/components/ui'
import { formatPrice } from '@/utils/formatting'
import type { CartItem } from '@/types/cart'

interface OrderSummaryPanelProps {
  items: CartItem[]
  subtotal: number
  totalItems: number
  taxRate: number | null
  deliveryFee: number | null
  tipAmount: number
  orderType: string | null
  grandTotal: number
  currency?: string
}

export function OrderSummaryPanel({
  items,
  subtotal,
  totalItems,
  taxRate,
  deliveryFee,
  tipAmount,
  orderType,
  grandTotal,
  currency,
}: OrderSummaryPanelProps) {
  return (
    <>
      {/* Cart Items */}
      <div className="space-y-4">
        {items.map((item) => (
          <div key={item.id} className="flex items-start gap-3">
            <div className="relative">
              <div className="h-16 w-16 rounded-md bg-muted overflow-hidden">
                {item.menuItem.image_url ? (
                  <img
                    src={item.menuItem.image_url}
                    alt={item.menuItem.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground">
                    No image
                  </div>
                )}
              </div>
              <div className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-muted-foreground text-white text-xs flex items-center justify-center">
                {item.quantity}
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{item.menuItem.name}</p>
              {item.selectedModifiers.length > 0 && (
                <p className="text-xs text-muted-foreground truncate">
                  {item.selectedModifiers.map(m => m.optionName).join(', ')}
                </p>
              )}
            </div>

            <span className="font-medium text-sm">{formatPrice(item.itemTotal, currency)}</span>
          </div>
        ))}
      </div>

      <Separator />

      {/* Totals */}
      <div className="space-y-2">
        <PriceLine
          label={`Subtotal · ${totalItems} items`}
          value={formatPrice(subtotal, currency)}
        />
        {taxRate != null && taxRate > 0 && (
          <PriceLine
            label={`Tax (${(taxRate * 100).toFixed(1)}%)`}
            value={formatPrice(Math.round(subtotal * taxRate * 100) / 100, currency)}
          />
        )}
        {orderType === 'delivery' && (
          <PriceLine
            label="Delivery"
            value={
              deliveryFee != null
                ? (deliveryFee === 0 ? 'Free' : formatPrice(deliveryFee, currency))
                : 'To be confirmed'
            }
          />
        )}
        {tipAmount > 0 && (
          <PriceLine label="Tip" value={formatPrice(tipAmount, currency)} />
        )}
      </div>

      <Separator />

      <PriceLine
        label="Total"
        value={formatPrice(grandTotal, currency)}
        variant="total"
      />
    </>
  )
}
